import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonModal,
  IonSearchbar,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { addCircle, checkmarkCircle, chevronForward, closeOutline, ellipseOutline, searchOutline } from 'ionicons/icons';
import { useEffect, useMemo, useRef, useState } from 'react';

export interface SearchPickerItem {
  id: string;
  label: string;
  sublabel?: string;
  details?: string[];
  badge?: string;
}

export interface SearchPickerModalProps {
  isOpen: boolean;
  title: string;
  items: SearchPickerItem[];
  selectedId?: string;
  /** I lotti conservano l’ordine cronologico deciso dalla pagina. */
  preserveOrder?: boolean;
  /** Dettagli completi multilinea; il badge resta sotto il testo. */
  wrapItems?: boolean;
  /** Mantiene il focus automatico sulla ricerca anche quando gli elementi vanno a capo. */
  focusSearchOnOpen?: boolean;
  /** Placeholder della barra di ricerca. */
  searchPlaceholder?: string;
  /** Mostrato quando non c'è proprio nessun elemento in lista. */
  emptyHint?: string;
  /** Azione primaria opzionale (es. "Crea nuovo alimento"), sempre in cima. */
  primaryAction?: { label: string; onClick: () => void };
  /** Modalità multi-selezione: checkbox + barra "Fatto". */
  multiple?: boolean;
  /** Ids selezionati (solo multiple). */
  selectedIds?: string[];
  /** Conferma multi-selezione (solo multiple). */
  onConfirm?: (ids: string[]) => void;
  /** Selezione singola (ignorato se multiple). */
  onSelect?: (id: string) => void;
  onClose: () => void;
}

/**
 * Campo "select" che apre il SearchPickerModal. Va messo dentro un IonItem
 * (mantiene il bordo lampeggiante te-field-*). Mostra il valore scelto o il
 * placeholder + chevron, look coerente con gli input stacked dell'app.
 */
export function PickerTriggerField(props: {
  label: string;
  value?: string;
  placeholder: string;
  onClick: () => void;
  /** T-243: se true il valore va a capo invece di troncarsi con "…" — per
   *  valori lunghi che devono restare leggibili (es. nome stampante + avviso). */
  wrapValue?: boolean;
}): JSX.Element {
  const { label, value, placeholder, onClick, wrapValue } = props;
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 6,
        width: '100%', background: 'transparent', border: 'none',
        padding: '10px 0', cursor: 'pointer', textAlign: 'left',
      }}
    >
      {label && (
        <span style={{ fontSize: 12, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
          {label}
        </span>
      )}
      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{
          fontSize: 15, color: value ? '#0f172a' : '#94a3b8',
          flex: 1, minWidth: 0,
          ...(wrapValue
            ? { whiteSpace: 'normal' as const, overflowWrap: 'anywhere' as const, lineHeight: 1.45 }
            : { whiteSpace: 'nowrap' as const, overflow: 'hidden', textOverflow: 'ellipsis' }),
        }}>
          {value || placeholder}
        </span>
        <IonIcon icon={chevronForward} style={{ fontSize: 18, color: '#94a3b8', flexShrink: 0 }} />
      </span>
    </button>
  );
}

// Rimuove accenti e abbassa: ricerca tollerante ("pere" trova "Però").
function norm(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
}

const MAX_RESULTS = 60;

/**
 * Selettore a tutto schermo con ricerca interattiva: filtra mentre si scrive.
 * Sostituisce le popup standard di IonSelect quando la lista può crescere
 * (ricette, alimenti). Tastiera aperta automaticamente sulla ricerca.
 */
export function SearchPickerModal(props: SearchPickerModalProps): JSX.Element {
  const { isOpen, title, items, selectedId, preserveOrder, wrapItems, focusSearchOnOpen, searchPlaceholder, emptyHint, primaryAction, multiple, selectedIds, onConfirm, onSelect, onClose } = props;
  const [q, setQ] = useState('');
  const [sel, setSel] = useState<Set<string>>(new Set());
  // Keep the contents through the closing animation, then stop preparing a
  // hidden list on every navigation of the cached Ionic page.
  const [closingContents, setClosingContents] = useState(isOpen);
  const showContents = isOpen || closingContents;
  const searchRef = useRef<HTMLIonSearchbarElement>(null);

  // Reset query + ripristino selezione multipla a ogni apertura.
  useEffect(() => {
    if (isOpen) {
      setClosingContents(true);
      setQ('');
      setSel(new Set(selectedIds ?? []));
    }
  }, [isOpen, selectedIds]);

  function confirmMulti(): void {
    onConfirm?.([...sel]);
    onClose();
  }

  const filtered = useMemo(() => {
    if (!showContents) return [];
    const nq = norm(q);
    const base = nq
      ? items.filter((it) => norm(it.label).includes(nq) || norm([it.sublabel, ...(it.details ?? [])].join(' ')).includes(nq))
      : items;
    const ordered = preserveOrder ? base : [...base].sort((a, b) =>
      a.label.localeCompare(b.label, 'it', { sensitivity: 'base', numeric: true }));
    return ordered.slice(0, MAX_RESULTS);
  }, [q, items, preserveOrder, showContents]);

  const overflow = useMemo(() => {
    if (!showContents) return 0;
    const nq = norm(q);
    const total = nq
      ? items.filter((it) => norm(it.label).includes(nq) || norm([it.sublabel, ...(it.details ?? [])].join(' ')).includes(nq)).length
      : items.length;
    return Math.max(0, total - MAX_RESULTS);
  }, [q, items, showContents]);

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={() => {
        setClosingContents(false);
        onClose();
      }}
      onDidPresent={() => {
        // Doppio rAF: la searchbar è montata dopo il present; così su mobile
        // si apre subito la tastiera.
        if (focusSearchOnOpen ?? !wrapItems) {
          requestAnimationFrame(() => requestAnimationFrame(() => { void searchRef.current?.setFocus(); }));
        }
      }}
    >
      {showContents && <>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{title}</IonTitle>
          <IonButtons slot="end">
            {multiple ? (
              <IonButton strong onClick={confirmMulti}>
                Fatto{sel.size > 0 ? ` (${sel.size})` : ''}
              </IonButton>
            ) : (
              <IonButton onClick={onClose} aria-label="Chiudi">
                <IonIcon icon={closeOutline} slot="icon-only" />
              </IonButton>
            )}
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent style={{ '--background': '#eef2f7' } as React.CSSProperties}>
        <div style={{
          position: 'sticky', top: 0, zIndex: 10,
          background: '#eef2f7', padding: '8px 10px 4px',
        }}>
          <IonSearchbar
            ref={searchRef}
            value={q}
            debounce={80}
            placeholder={searchPlaceholder ?? 'Cerca…'}
            onIonInput={(e) => setQ(String(e.detail.value ?? ''))}
            style={{
              '--background': '#fff',
              '--border-radius': '12px',
              '--box-shadow': '0 1px 3px rgba(15,23,42,.08)',
              paddingTop: 0, paddingBottom: 0,
            } as React.CSSProperties}
          />
        </div>

        <div style={{ padding: '4px 12px 24px' }}>
          {primaryAction && (
            <button
              type="button"
              onClick={primaryAction.onClick}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                width: '100%', marginBottom: 10,
                background: '#16a34a', color: '#fff',
                border: 'none', borderRadius: 12, padding: '13px 14px',
                fontSize: 15, fontWeight: 700, cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(22,163,74,.35)',
              }}
            >
              <IonIcon icon={addCircle} style={{ fontSize: 20 }} />
              {primaryAction.label}
            </button>
          )}

          {items.length === 0 ? (
            <div style={{
              background: '#fff', borderRadius: 14, padding: '28px 18px',
              textAlign: 'center', color: '#94a3b8', fontSize: 14,
            }}>
              {emptyHint ?? 'Nessun elemento disponibile.'}
            </div>
          ) : filtered.length === 0 ? (
            <div style={{
              background: '#fff', borderRadius: 14, padding: '28px 18px',
              textAlign: 'center', color: '#64748b', fontSize: 14,
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
            }}>
              <IonIcon icon={searchOutline} style={{ fontSize: 30, color: '#cbd5e1' }} />
              Nessun risultato per “{q}”.
            </div>
          ) : (
            <div style={{
              background: '#fff', borderRadius: 14, overflow: 'hidden',
              boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
            }}>
              {filtered.map((it, i) => {
                const isSel = multiple ? sel.has(it.id) : it.id === selectedId;
                return (
                  <button
                    key={it.id}
                    type="button"
                    onClick={() => {
                      if (multiple) {
                        setSel((prev) => {
                          const next = new Set(prev);
                          if (next.has(it.id)) next.delete(it.id);
                          else next.add(it.id);
                          return next;
                        });
                      } else {
                        onSelect?.(it.id);
                      }
                    }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      width: '100%', textAlign: 'left',
                      background: isSel ? '#eff6ff' : '#fff',
                      border: 'none',
                      borderTop: i === 0 ? 'none' : '1px solid #f1f5f9',
                      padding: '14px 14px', cursor: 'pointer',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 15, fontWeight: 600, color: '#0f172a',
                        ...(wrapItems
                          ? { whiteSpace: 'normal' as const, overflowWrap: 'anywhere' as const, lineHeight: 1.45 }
                          : { whiteSpace: 'nowrap' as const, overflow: 'hidden', textOverflow: 'ellipsis' }),
                      }}>
                        {it.label}
                      </div>
                      {it.details?.map((detail, index) => (
                        <div key={index} style={{ fontSize: 14, color: '#475569', marginTop: 4, whiteSpace: 'normal', overflowWrap: 'anywhere', lineHeight: 1.5 }}>
                          {detail}
                        </div>
                      ))}
                      {it.sublabel && (
                        <div style={{
                          fontSize: 12, color: '#64748b', marginTop: 2,
                          ...(wrapItems
                            ? { whiteSpace: 'normal' as const, overflowWrap: 'anywhere' as const, lineHeight: 1.5 }
                            : { whiteSpace: 'nowrap' as const, overflow: 'hidden', textOverflow: 'ellipsis' }),
                        }}>
                          {it.sublabel}
                        </div>
                      )}
                      {wrapItems && it.badge && (
                        <span style={{ display: 'inline-block', marginTop: 6, background: '#f1f5f9', color: '#475569', fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 999, overflowWrap: 'anywhere' }}>
                          {it.badge}
                        </span>
                      )}
                    </div>
                    {!wrapItems && it.badge && (
                      <span style={{
                        background: '#f1f5f9', color: '#475569',
                        fontSize: 11, fontWeight: 600,
                        padding: '3px 8px', borderRadius: 999, flexShrink: 0,
                      }}>
                        {it.badge}
                      </span>
                    )}
                    <IonIcon
                      icon={isSel ? checkmarkCircle : (multiple ? ellipseOutline : chevronForward)}
                      style={{ fontSize: isSel ? 22 : 18, color: isSel ? '#0066cc' : '#cbd5e1', flexShrink: 0 }}
                    />
                  </button>
                );
              })}
            </div>
          )}

          {overflow > 0 && (
            <div style={{ textAlign: 'center', fontSize: 12, color: '#94a3b8', marginTop: 12 }}>
              +{overflow} altri — affina la ricerca per restringere.
            </div>
          )}
        </div>
      </IonContent>
      </>}
    </IonModal>
  );
}
